import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CONFIG_URLS_API } from 'src/environments/config-envs';

import {
  SwapApiResponse,
  SwapCombustivel,
  SwapGravarSessaoRequest,
  SwapGravarSessaoResponse,
  SwapImagemSessao,
  SwapImagemTipo,
  SwapPreAutorizaRequest,
  SwapPreAutorizaResponse,
  SwapSessao,
  SwapSessaoConsultaFiltro,
  SwapSessaoConsultaResultado,
} from '../model/swap';

/**
 * Cliente HTTP para o `SwapController` (ctf-services).
 *
 * Endpoints (base = `environment.host_ctf_service + CONFIG_URLS_API.API_SWAP`):
 *  - POST `/ListarSessoes?guid=...`               → SwapSessaoConsultaResultado
 *  - GET  `/ObterSessao?guid=...&sessionExterna=...` → SwapSessao (posto no JSON:
 *    `codPosto` / `nomePosto`; na proc continuam `COD_POST` / `PA7_NOME`.)
 *  - GET  `/ObterImagemSessao?guid=...&sessionExterna=...&selImg=...` → SwapImagemSessao
 *  - GET  `/Combustiveis?guid=...&placa=...` → SwapCombustivel[]
 *      `placa` é obrigatória — a proc filtra pelos combustíveis elegíveis
 *      para o veículo (regras de aceitação do contrato/frota).
 *  - POST `/GravarSessao?guid=...`                 → SwapGravarSessaoResponse (SEM envelope).
 *      Body inclui `bypass?: boolean`. Quando `true`, o backend já aplica o
 *      bypass na mesma chamada e devolve o resultado em `response.bypass`.
 *  - POST `/IncluirAutorizacaoManual?guid=...`      → SwapPreAutorizaResponse (SEM envelope).
 *      Body: `{ sessionExterna }`. NEGADO/ERRO chegam como HTTP 200
 *      com `statusRetorno` preenchido — não são erro técnico.
 *
 * Convenção de autenticação: `guid` do usuário sempre como querystring,
 * idêntico ao padrão usado em `GerenciarCotaService`.
 */
@Injectable({
  providedIn: 'root',
})
export class SwapService {
  private readonly httpOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  private readonly baseUrl =
    environment.host_ctf_service + CONFIG_URLS_API.API_SWAP;

  constructor(private http: HttpClient) {}

  /**
   * Lista paginada de sessões SWAP (`PR_SWAP_SESSAO_CONSULTA`).
   * Cada item (`SwapSessao`) inclui `habilitaByPass` (BIT `HabilitaByPass` na proc).
   * O modelo de lista normaliza em `flgPermiteBypass` / `bypassDisponivel`.
   * Usa `flgAutorizado` para exibir **Repetir**.
   *
   * @param userGuid GUID do usuário autenticado.
   * @param filtro   Filtros + ordenação + paginação. Quando
   *                 `pageNumber`/`rowsPage` forem `null`, a proc
   *                 retorna todos os registros.
   */
  listarSessoes(
    userGuid: string,
    filtro: SwapSessaoConsultaFiltro,
  ): Observable<SwapApiResponse<SwapSessaoConsultaResultado>> {
    const url = `${this.baseUrl}/ListarSessoes?guid=${userGuid}`;
    return this.http.post<SwapApiResponse<SwapSessaoConsultaResultado>>(
      url,
      filtro,
      this.httpOptions,
    );
  }

  /**
   * Detalhe de uma sessão (`PR_SWAP_SESSAO_CONSULTA_ITEM`).
   * O retorno inclui `habilitaByPass` com a mesma regra de bypass da listagem.
   *
   * @param userGuid       GUID do usuário autenticado (sessão da aplicação).
   * @param sessionExterna Identificador externo da sessão (`DSC_SESSION_EXTERNA`).
   */
  obterSessao(
    userGuid: string,
    sessionExterna: string,
  ): Observable<SwapApiResponse<SwapSessao>> {
    const url = `${this.baseUrl}/ObterSessao`;
    const params = new HttpParams()
      .set('guid', userGuid)
      .set('sessionExterna', sessionExterna);

    return this.http.get<SwapApiResponse<SwapSessao>>(url, {
      ...this.httpOptions,
      params,
    });
  }

  /**
   * Obtém uma das imagens da sessão (BOMBA / PANEL / PLACA) —
   * `PR_SWAP_SESSAO_OBTEM_IMAGEM_ITEM`.
   *
   * Atenção ao parâmetro: a API usa `'PANEL'` (inglês), embora a coluna
   * física no banco seja `IMG_PAINEL`. O backend já faz a tradução; no
   * front sempre `'PANEL'`.
   *
   * Interpretação do retorno (mesmo padrão do `obterSessao`):
   *  - `res.sucesso === false`           → erro (mensagens em `res.mensagens`).
   *  - `res.retorno == null` & `sucesso` → sessão não encontrada OU usuário
   *                                         sem permissão na frota da sessão.
   *  - `res.retorno?.imgBase64 == null`  → a sessão existe, mas a imagem
   *                                         está vazia/`NULL` no banco.
   *  - caso contrário                    → use `montarDataUrlImagemSessao`
   *                                         para gerar o `data:` URL.
   *
   * @param userGuid       GUID do usuário autenticado (sessão da aplicação).
   * @param sessionExterna Identificador externo da sessão (`DSC_SESSION_EXTERNA`).
   * @param selImg         Tipo de imagem desejada: 'BOMBA' | 'PANEL' | 'PLACA'.
   */
  obterImagemSessao(
    userGuid: string,
    sessionExterna: string,
    selImg: SwapImagemTipo,
  ): Observable<SwapApiResponse<SwapImagemSessao>> {
    const url = `${this.baseUrl}/ObterImagemSessao`;
    const params = new HttpParams()
      .set('guid', userGuid)
      .set('sessionExterna', sessionExterna)
      .set('selImg', selImg);

    return this.http.get<SwapApiResponse<SwapImagemSessao>>(url, {
      ...this.httpOptions,
      params,
    });
  }

  /**
   * Busca os combustíveis elegíveis para uma placa
   * (`PR_SWAP_BUSCA_COMBUSTIVEL_PORTAL`).
   *
   * Endpoint: `GET /api/Swap/Combustiveis?guid={guid}&placa={placa}`.
   * A proc no backend recebe `@COD_ACESSO` (do guid) e `@PAW_PLACA`
   * (até 7 caracteres) e devolve apenas os combustíveis aceitos pelo
   * contrato/frota daquela placa.
   *
   * Erros de negócio (proc retorna `STATUS='ERRO'`) são empacotados
   * pelo backend como `Sucesso=false` + `Mensagens` — chegam como
   * HTTP 200 e devem ser tratados no `.subscribe()` (não em `catchError`).
   *
   * @param userGuid GUID do usuário autenticado.
   * @param placa    Placa do veículo (obrigatória). Deve ser normalizada
   *                 pelo chamador (UPPER + apenas A-Z/0-9), pois o
   *                 backend trunca em 7 caracteres.
   */
  buscarCombustiveis(
    userGuid: string,
    placa: string,
  ): Observable<SwapApiResponse<SwapCombustivel[]>> {
    const url = `${this.baseUrl}/Combustiveis`;
    const params = new HttpParams()
      .set('guid', userGuid)
      .set('placa', placa);

    return this.http.get<SwapApiResponse<SwapCombustivel[]>>(url, {
      ...this.httpOptions,
      params,
    });
  }

  /**
   * Grava a sessão de autorização manual no banco
   * (`PR_SWAP_INSERE_PREAUTORIZACAO_PORTAL`) e, opcionalmente, aplica o
   * bypass (`PR_APP_GESTOR_INS_BYPASS`) na **mesma chamada** quando
   * `request.bypass === true`.
   *
   * Esta chamada apenas PERSISTE a sessão (e o bypass, se solicitado).
   * A efetivação para processamento é feita posteriormente via
   * `incluirAutorizacaoManual`.
   *
   * IMPORTANTE: este endpoint NÃO usa o envelope `SwapApiResponse<T>`.
   * O backend retorna `Ok(processamento.Retorno)`, ou seja, o objeto
   * `SwapGravarSessaoResponse` direto. Erros de validação ('0422') e
   * internos ('0500') são lançados como exception no backend e chegam
   * aqui como HTTP 4xx/5xx — trate com `catchError`.
   *
   * Sucesso possui dois cenários (verifique `proximaEtapa`):
   *  - 'PRE_AUTORIZACAO' → sessão de autorização manual criada normalmente.
   *  - 'SESSAO_EXISTENTE' → sessão duplicada (apenas alguns campos
   *    vêm preenchidos). O backend **não aplica** bypass nesse caso.
   *
   * O resultado do bypass (quando solicitado) vem em
   * `response.bypass`:
   *  - `null`  → bypass não foi solicitado (request.bypass falsy).
   *  - `!null` → bypass foi tentado; `bypass.flgSucesso` indica se
   *              aplicou (true) ou falhou (false). Em caso de falha,
   *              a gravação NÃO é desfeita — mostre `bypass.dscRetorno`
   *              em aviso (amarelo) ao usuário.
   *
   * Por segurança, o flag `bypass` viaja **no body** (não na query
   * string) — query strings aparecem em logs de IIS, proxies, header
   * `Referer`, etc.
   */
  gravarSessao(
    userGuid: string,
    request: SwapGravarSessaoRequest,
  ): Observable<SwapGravarSessaoResponse> {
    const url = `${this.baseUrl}/GravarSessao`;
    const params = new HttpParams().set('guid', userGuid);
    return this.http.post<SwapGravarSessaoResponse>(url, request, {
      ...this.httpOptions,
      params,
    });
  }

  /**
   * Efetiva a autorização manual na BIS-SWAP via ctf-services.
   *
   * É a etapa FINAL do fluxo de autorização manual, executada DEPOIS de
   * `gravarSessao` (que já cuida do bypass quando solicitado). O backend
   * `ctf-services` trata o OAuth2 (token cacheado pelo `IBisSwapTokenProvider`)
   * e o repasse para a API externa BIS-SWAP — o front nunca vê o token.
   *
   * IMPORTANTE: este endpoint NÃO usa o envelope `SwapApiResponse<T>`;
   * o controller faz `Ok(processamento.Retorno)`, devolvendo o objeto
   * `SwapPreAutorizaResponse` direto. Erros 4xx/5xx (validação, infra,
   * 401 mesmo após retentativa) chegam como HTTP error e devem ser
   * tratados com `catchError`. NÃO retentar automaticamente em 400/500.
   *
   * Interpretação do retorno (mesma regra documentada no controller):
   *  - `statusRetorno === 'OK' && flgAutorizado === 'S'` → autorizado.
   *  - `statusRetorno === 'NEGADO'` → recusa de negócio; mostrar
   *    `mesangemErro`/`msgErroPOS`. **NÃO** é erro técnico — chega
   *    como HTTP 200 e NÃO deve cair no `catchError`.
   *  - `statusRetorno === 'ERRO'`   → erro reportado pela BIS-SWAP;
   *    mostrar `mesangemErro`/`msgErroPOS`. Também HTTP 200.
   *
   * Use os helpers `isSwapAutorizacaoManualOk` e
   * `extrairMensagemSwapAutorizacaoManual` no `.subscribe()` para distinguir
   * sucesso real de erro de negócio.
   *
   * O nome `mesangemErro` (com typo) é proposital: faz parte do contrato
   * da API real.
   */
  incluirAutorizacaoManual(
    userGuid: string,
    request: SwapPreAutorizaRequest,
  ): Observable<SwapPreAutorizaResponse> {
    const url = `${this.baseUrl}/IncluirAutorizacaoManual`;
    const params = new HttpParams().set('guid', userGuid);
    return this.http.post<SwapPreAutorizaResponse>(url, request, {
      ...this.httpOptions,
      params,
    });
  }
}
